/**
 * CareNarrativeSections.jsx — Feb 2026 · Michael's CARE repositioning
 *
 * One Million Lights, retold as a global thank-you to unseen carers.
 *
 * These sections sit BELOW the arrival hero/globe on the OML landing
 * page and carry the emotional story. They are read-only, kept
 * component-scoped, and use the existing OML palette so the file can
 * be replaced or reshuffled without touching the payment or globe
 * logic.
 *
 * Sections (in narrative order):
 *   1. LiveImpactBand          — X / 1,000,000 lights + separated counters
 *   2. RippleEffectSection     — one light travels further than we imagine
 *   3. BehindEveryPersonSection— the story of the unseen carer
 *   4. CircleOfCarePreview     — three signature Foundations experiences
 *   5. LittleCareForYouCard    — the signature carer moment (placeholder)
 *   6. PartnershipSection      — help us reach unseen carers in your community
 *   7. TransparencySection     — where the £1 goes, honestly
 *   8. FoundingLightThankYou   — quiet secondary tier (40% off ecosystem)
 *   9. ClosingMillionCarers    — a million thank-yous, glowing world
 *
 * Every section exports a single component; they are composed in
 * order inside OneMillionLightsPage.jsx.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  cream: "#f5ecd9",
  creamReadable: "rgba(245,236,217,0.92)",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.92)",
  goldMist: "rgba(224,169,122,0.22)",
  navySoft: "rgba(19,31,56,0.55)",
};

/* Shared section wrapper for consistent spacing + soft top rule. */
function Section({ children, id, testId, first = false }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className={first ? "mt-16" : "mt-20"}
      style={{
        paddingTop: 8,
      }}
    >
      {children}
    </section>
  );
}

function EyebrowLabel({ children }) {
  return (
    <div
      className="uppercase text-center"
      style={{
        color: TOK.goldSoft,
        fontFamily: '"Manrope", sans-serif',
        fontSize: "0.6rem",
        letterSpacing: "0.34em",
      }}
    >
      ✦ {children} ✦
    </div>
  );
}

function SectionHeadline({ children, testId }) {
  return (
    <h2
      data-testid={testId}
      className="mt-4 text-center italic leading-tight"
      style={{
        color: TOK.cream,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "clamp(1.6rem, 5.4vw, 2.4rem)",
        fontWeight: 300,
      }}
    >
      {children}
    </h2>
  );
}

function BodyCopy({ children, testId }) {
  return (
    <p
      data-testid={testId}
      className="mt-4 text-center leading-relaxed max-w-xl mx-auto"
      style={{
        color: TOK.creamReadable,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "clamp(1.02rem, 3.2vw, 1.2rem)",
      }}
    >
      {children}
    </p>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 1. Live Impact Band — X / 1,000,000 + separated counters
 *
 * Pulls from a lightweight /api/one-million-lights/impact endpoint
 * with a graceful fallback so the band always renders. Only
 * activated lights count toward the 1M total per the master prompt.
 * ────────────────────────────────────────────────────────────── */
export function LiveImpactBand({ count }) {
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/one-million-lights/impact`);
        if (!cancelled && r.data) setImpact(r.data);
      } catch (_) {
        // silent — we fall back to /count if impact isn't available yet
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Feb 22 2026 · Michael's mobile review — the arrival hero and Live
  // Impact showed different numbers (18,406 vs 1). Sync both to the
  // same source of truth (`count.display`). The Live Impact then
  // breaks that same total into sub-metrics (carers invited, etc.)
  // instead of contradicting the headline.
  const placed = count?.display ?? impact?.lights_placed ?? impact?.lights_activated ?? count?.total ?? 0;
  const target = 1_000_000;
  const pct = Math.min(1, placed / target);

  const stats = [
    { label: "Lights shining", value: placed },
    { label: "Carers invited", value: impact?.carers_invited ?? 0 },
    { label: "Foundations activated", value: impact?.foundations_activated ?? 0 },
    { label: "Moments of Light shared", value: impact?.moments_shared ?? 0 },
    { label: "Countries reached", value: impact?.countries ?? count?.countries_count ?? 0 },
  ];

  return (
    <Section testId="oml-live-impact" first>
      <EyebrowLabel>Live impact</EyebrowLabel>
      <div
        className="mt-6 text-center"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(2.6rem, 8vw, 3.8rem)",
          fontWeight: 500,
          letterSpacing: "0.01em",
          lineHeight: 1,
        }}
        data-testid="oml-impact-ratio"
      >
        <span style={{ color: TOK.gold }}>
          {placed.toLocaleString("en-GB")}
        </span>
        <span style={{ color: TOK.creamMist, margin: "0 12px", fontSize: "0.6em" }}>/</span>
        <span style={{ color: TOK.creamReadable, fontSize: "0.7em" }}>
          1,000,000
        </span>
      </div>
      <div
        className="mt-2 text-center uppercase"
        style={{
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.6rem",
          letterSpacing: "0.34em",
        }}
      >
        lights placed
      </div>
      {/* Progress line */}
      <div
        className="mt-6 mx-auto rounded-full overflow-hidden"
        style={{
          maxWidth: 460,
          height: 4,
          background: "rgba(245,236,217,0.08)",
        }}
      >
        <div
          style={{
            width: `${Math.max(0.6, pct * 100)}%`,
            height: "100%",
            background: "linear-gradient(90deg, #f0c790 0%, #e0a97a 100%)",
            boxShadow: "0 0 12px rgba(224,169,122,0.55)",
            transition: "width 900ms ease",
          }}
        />
      </div>
      {/* Sub-counters — a soft grid, one per figure. */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-2xl mx-auto">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl px-4 py-3 text-center"
            style={{
              background: "rgba(245,236,217,0.035)",
              border: `1px solid ${TOK.goldMist}`,
            }}
            data-testid={`oml-impact-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div
              style={{
                color: TOK.gold,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.3rem",
                fontWeight: 500,
                lineHeight: 1.1,
              }}
            >
              {(s.value || 0).toLocaleString("en-GB")}
            </div>
            <div
              className="mt-1 uppercase"
              style={{
                color: TOK.creamMist,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.5rem",
                letterSpacing: "0.22em",
                lineHeight: 1.4,
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 2. Ripple Effect Section
 * ────────────────────────────────────────────────────────────── */
export function RippleEffectSection() {
  const stages = [
    "Someone gives a light",
    "A carer receives support",
    "The carer feels calmer, more supported",
    "They share a Moment of Light",
    "Your light touches an entire circle",
  ];
  return (
    <Section testId="oml-ripple-effect">
      <EyebrowLabel>The ripple effect</EyebrowLabel>
      <SectionHeadline testId="oml-ripple-headline">
        One light can travel further<br />
        <span style={{ color: TOK.gold }}>than we imagine.</span>
      </SectionHeadline>
      <BodyCopy>
        You are not simply supporting one person. You are supporting someone
        who may be caring for a parent, partner, child or vulnerable person
        every day. Through them, your light can touch an entire circle of lives.
      </BodyCopy>

      {/* Concentric ripples — pure CSS, respects reduced motion elsewhere */}
      <div className="mt-10 flex justify-center" aria-hidden>
        <div style={{ position: "relative", width: 260, height: 260 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: "50%",
                top: "50%",
                width: 60 + i * 55,
                height: 60 + i * 55,
                transform: "translate(-50%, -50%)",
                border: `1px solid ${i === 0 ? TOK.gold : "rgba(224,169,122,0.28)"}`,
                background: i === 0
                  ? "radial-gradient(circle, #fff7e6 0%, #e0a97a 55%, rgba(224,169,122,0) 100%)"
                  : "transparent",
                boxShadow: i === 0 ? "0 0 42px rgba(224,169,122,0.6)" : "none",
                opacity: 1 - i * 0.18,
                animation: `omlRipple 4200ms ease-in-out ${i * 700}ms infinite`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Stages */}
      <ol className="mt-8 max-w-md mx-auto space-y-3">
        {stages.map((s, i) => (
          <li
            key={i}
            data-testid={`oml-ripple-stage-${i}`}
            className="flex items-start gap-3"
          >
            <span
              className="inline-flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                width: 26,
                height: 26,
                background: "rgba(224,169,122,0.16)",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.gold,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.65rem",
                fontWeight: 600,
              }}
            >
              {i + 1}
            </span>
            <span
              className="italic pt-[3px]"
              style={{
                color: TOK.creamReadable,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.05rem",
              }}
            >
              {s}
            </span>
          </li>
        ))}
      </ol>
      <div
        className="mt-10 text-center italic"
        style={{
          color: TOK.gold,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.05rem, 3.4vw, 1.25rem)",
          letterSpacing: "0.02em",
        }}
      >
        One light. One carer. Many lives touched.
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 3. Behind Every Person — the story section
 * ────────────────────────────────────────────────────────────── */
export function BehindEveryPersonSection({ onGiveLight }) {
  return (
    <Section testId="oml-behind-every-person">
      <EyebrowLabel>The people we forget to thank</EyebrowLabel>
      <SectionHeadline testId="oml-story-headline">
        Behind every person being cared for<br />
        <span style={{ color: TOK.gold }}>is someone who needs care too.</span>
      </SectionHeadline>

      <div className="mt-6 max-w-xl mx-auto space-y-4">
        {[
          "Every day, unseen carers give their time, strength and love to someone who needs them.",
          "Some care for an elderly parent. Some support a partner through illness. Some raise children with additional needs. Others quietly help a relative, friend or neighbour through an incredibly difficult time.",
          "They do not always call themselves carers.",
          "They simply continue showing up.",
          "One Million Lights exists to recognise them, support them, and say the two words they may not hear often enough:",
        ].map((line, i) => (
          <p
            key={i}
            className="text-center leading-relaxed"
            style={{
              color: TOK.creamReadable,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1rem, 3.2vw, 1.18rem)",
            }}
          >
            {line}
          </p>
        ))}
      </div>

      <div
        className="mt-6 text-center italic"
        style={{
          color: TOK.gold,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.8rem, 6vw, 2.4rem)",
          fontWeight: 300,
          letterSpacing: "0.02em",
        }}
      >
        Thank you.
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={onGiveLight}
          data-testid="oml-story-give-light"
          className="rounded-full transition-all"
          style={{
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.82rem",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "18px 32px",
            boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          Place a Light — £1
        </button>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 4. Circle of Care Preview — the exact new Foundations offering
 *
 * Feb 17 2026 · Michael. The Gift reveal must show what carers
 * actually receive, not a generic story. So this now reflects the
 * real Foundations catalogue: three daily rituals (Morning · Peace ·
 * Rest), the three signature care experiences (Kindness · Not Alone
 * · Difficult Day), the 60–90 s Resets, and the shared 8-hour Night
 * companion. One card lets the visitor hear a 9-second sample of
 * "A Little Kindness for Your Body" — the emotional heart of the
 * gift — right on the page.
 * ────────────────────────────────────────────────────────────── */
export function CircleOfCarePreview() {
  const daily = [
    { title: "Morning Light",   len: "3 & 7 min", body: "Begin the day with a little more space, calm and light." },
    { title: "Peace",           len: "3 & 7 min", body: "A soft return to your own centre when the noise gets loud." },
    { title: "Rest",            len: "3 & 7 min", body: "Gently put the day down. Nothing to fix here." },
  ];
  const signature = [
    { title: "A Little Kindness for Your Body", len: "6 min", body: "Your body carries so much for you. Give it a little kindness.", sample: "found_kindness_for_body" },
    { title: "You Are Not Alone",               len: "1 min", body: "Companion energy for the quiet moments. Not therapy — presence." },
    { title: "For a Difficult Day",             len: "1 min", body: "The one to press play on when everything is going wrong." },
  ];
  const resets = ["Breathe", "Smile", "Ground"];
  return (
    <Section testId="oml-circle-of-care">
      <EyebrowLabel>Soulumo Foundations</EyebrowLabel>
      <SectionHeadline testId="oml-circle-headline">
        A foundation<br />
        <span style={{ color: TOK.gold }}>of care.</span>
      </SectionHeadline>
      <div
        className="mt-3 text-center italic"
        style={{
          color: TOK.gold,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.05rem, 3.4vw, 1.25rem)",
          letterSpacing: "0.02em",
        }}
        data-testid="oml-circle-tagline"
      >
        Care for your body. Care for your mind. Care for you.
      </div>
      <BodyCopy>
        Your light opens a small, curated place where a carer can pause,
        breathe, rest and receive something for themselves. Here is
        what waits for them.
      </BodyCopy>

      {/* Daily rituals — three duals. */}
      <div className="mt-10">
        <div
          className="uppercase text-center mb-4"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.32em",
          }}
        >
          Daily rituals · 3 & 7 minutes
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
          {daily.map((c) => (
            <div
              key={c.title}
              className="rounded-3xl p-5"
              style={{
                background: "rgba(19,31,56,0.55)",
                border: `1px solid ${TOK.goldMist}`,
              }}
              data-testid={`oml-daily-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="uppercase"
                style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.5rem", letterSpacing: "0.3em" }}
              >
                {c.len}
              </div>
              <h3
                className="mt-2 italic"
                style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.3rem", fontWeight: 400, lineHeight: 1.2 }}
              >
                {c.title}
              </h3>
              <p
                className="mt-2"
                style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem", lineHeight: 1.55 }}
              >
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Signature care experiences — including the sample player. */}
      <div className="mt-10">
        <div
          className="uppercase text-center mb-4"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.32em",
          }}
        >
          Signature care experiences
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
          {signature.map((c) => (
            <div
              key={c.title}
              className="rounded-3xl p-5 flex flex-col"
              style={{
                background: c.sample
                  ? "linear-gradient(180deg, rgba(224,169,122,0.14) 0%, rgba(19,31,56,0.55) 100%)"
                  : "rgba(19,31,56,0.55)",
                border: `1px solid ${c.sample ? TOK.gold : TOK.goldMist}`,
                boxShadow: c.sample ? "0 0 40px rgba(224,169,122,0.2)" : "none",
              }}
              data-testid={`oml-signature-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="uppercase"
                style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.5rem", letterSpacing: "0.3em" }}
              >
                {c.len}
              </div>
              <h3
                className="mt-2 italic"
                style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.25rem", fontWeight: 400, lineHeight: 1.2 }}
              >
                {c.title}
              </h3>
              <p
                className="mt-2 flex-1"
                style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem", lineHeight: 1.55 }}
              >
                {c.body}
              </p>
              {c.sample && (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${TOK.goldMist}` }}>
                  <SampleTasteButton itemId={c.sample} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Resets strip + Night companion. */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
        <div
          className="rounded-3xl p-5"
          style={{ background: "rgba(19,31,56,0.55)", border: `1px solid ${TOK.goldMist}` }}
          data-testid="oml-resets-preview"
        >
          <div
            className="uppercase"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.5rem", letterSpacing: "0.3em" }}
          >
            60-90 s Resets
          </div>
          <h3
            className="mt-2 italic"
            style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.15rem", fontWeight: 400, lineHeight: 1.2 }}
          >
            A doorway back to centre in 90 seconds.
          </h3>
          <div className="mt-3 flex gap-2 flex-wrap">
            {resets.map((r) => (
              <span
                key={r}
                className="rounded-full px-3 py-1"
                style={{
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.cream,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "0.96rem",
                  fontStyle: "italic",
                }}
              >
                {r}
              </span>
            ))}
          </div>
        </div>
        <div
          className="rounded-3xl p-5"
          style={{ background: "rgba(19,31,56,0.55)", border: `1px solid ${TOK.goldMist}` }}
          data-testid="oml-night-preview"
        >
          <div
            className="uppercase"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif', fontSize: "0.5rem", letterSpacing: "0.3em" }}
          >
            Shared master · 8 hours
          </div>
          <h3
            className="mt-2 italic"
            style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.15rem", fontWeight: 400, lineHeight: 1.2 }}
          >
            Foundations Night.
          </h3>
          <p
            className="mt-2"
            style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.96rem", lineHeight: 1.5 }}
          >
            An eight-hour companion at whisper level for the whole night — words that reach quietly under sleep.
          </p>
        </div>
      </div>

      <div
        className="mt-10 text-center italic max-w-xl mx-auto"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.15rem, 3.6vw, 1.4rem)",
          fontWeight: 300,
        }}
      >
        “You spend so much of your life caring for others.
        <br />
        <span style={{ color: TOK.gold }}>
          This is where some of that care comes back to you.”
        </span>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * SampleTasteButton — a 9-second taster of a Foundations track
 *
 * Feb 17 2026 · Michael. Right there on the OML Gift page, the
 * visitor can press "Hear a moment" to sample "A Little Kindness
 * for Your Body". We play 9 seconds from ~60s in (past the intro
 * so it lands on the warmest part of the reading), then fade
 * gently out. Cost: nothing — reuses the already-cached audio.
 * ────────────────────────────────────────────────────────────── */
function SampleTasteButton({ itemId }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} }
    };
  }, []);

  const play = async () => {
    if (playing) return;
    // Resolve the current audio_url from the manifest. If we cannot
    // (session expired), we simply do nothing — this is a taster,
    // not a critical flow.
    try {
      const r = await axios.get(`${API}/api/one-million-lights/sample-clip?item_id=${itemId}`);
      const url = r.data?.audio_url;
      if (!url) return;
      const a = new Audio(`${API}${url}`);
      a.preload = "auto";
      audioRef.current = a;
      a.addEventListener("canplay", () => {
        try { a.currentTime = Math.min(60, (a.duration || 60) * 0.15); } catch (_) {}
        a.play().catch(() => {});
        setPlaying(true);
        // 9-second bar
        const startedAt = Date.now();
        timerRef.current = setInterval(() => {
          const t = (Date.now() - startedAt) / 1000;
          setProgress(Math.min(9, t));
          if (t >= 8) {
            // Fade out over 1 s
            const startVol = a.volume;
            const fadeStart = Date.now();
            const fade = setInterval(() => {
              const ft = (Date.now() - fadeStart) / 1000;
              a.volume = Math.max(0, startVol * (1 - ft));
              if (ft >= 1) {
                clearInterval(fade);
                a.pause();
                setPlaying(false);
                setProgress(0);
                clearInterval(timerRef.current);
              }
            }, 40);
          }
        }, 100);
      }, { once: true });
    } catch (_) {
      // silent
    }
  };

  const stop = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} }
    if (timerRef.current) clearInterval(timerRef.current);
    setPlaying(false);
    setProgress(0);
  };

  return (
    <button
      onClick={playing ? stop : play}
      data-testid={`oml-sample-${itemId}`}
      className="w-full rounded-full transition-all flex items-center justify-center gap-2"
      style={{
        background: playing ? "rgba(224,169,122,0.22)" : "transparent",
        border: `1px solid ${TOK.gold}`,
        color: TOK.gold,
        fontFamily: '"Manrope", sans-serif',
        fontSize: "0.62rem",
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        fontWeight: 600,
        padding: "12px 16px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {playing ? "✦  Playing a moment…" : "▶  Hear a moment"}
      {playing && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            height: 2,
            width: `${(progress / 9) * 100}%`,
            background: TOK.gold,
            boxShadow: "0 0 6px rgba(224,169,122,0.6)",
            transition: "width 100ms linear",
          }}
        />
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 4b. Unifying Essence — the whole ecosystem, in one sentence
 *
 * Sits between the Circle of Care and Partnership sections. Quiet,
 * spacious, and deliberately without a CTA — this is the moment the
 * page holds its breath and states its purpose.
 * ────────────────────────────────────────────────────────────── */
export function UnifyingEssenceSection() {
  return (
    <Section testId="oml-unifying-essence">
      <div
        className="mx-auto text-center rounded-3xl px-6 py-14"
        style={{
          maxWidth: 640,
          background: "linear-gradient(180deg, rgba(224,169,122,0.06) 0%, rgba(10,19,42,0.35) 100%)",
          border: `1px solid ${TOK.goldMist}`,
        }}
      >
        <EyebrowLabel>One movement · One promise</EyebrowLabel>
        <div
          className="mt-6 italic leading-tight"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.4rem, 4.6vw, 1.9rem)",
            fontWeight: 300,
          }}
          data-testid="oml-essence-line"
        >
          One Million Lights is creating a
          {" "}<span style={{ color: TOK.gold }}>foundation of care</span>
          {" "}for the people who care for others
          <br />
          <span style={{ color: TOK.creamReadable }}>
            — and for everyone within their Circle of Care.
          </span>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
          {[
            { k: "One Million Lights", v: "shares the care" },
            { k: "Soulumo Foundations", v: "provides the care" },
            { k: "Carers & their circles", v: "receive the care" },
            { k: "Moments of Light", v: "let the care travel" },
          ].map((row) => (
            <div
              key={row.k}
              className="rounded-2xl px-4 py-3 text-left"
              style={{
                background: "rgba(245,236,217,0.04)",
                border: `1px solid ${TOK.goldMist}`,
              }}
              data-testid={`oml-essence-${row.k.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="uppercase"
                style={{
                  color: TOK.gold,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.55rem",
                  letterSpacing: "0.28em",
                  fontWeight: 600,
                }}
              >
                {row.k}
              </div>
              <div
                className="mt-1 italic"
                style={{
                  color: TOK.creamReadable,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.02rem",
                }}
              >
                {row.v}.
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 5. Partnership Section
 * ────────────────────────────────────────────────────────────── */
export function PartnershipSection() {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    org: "",
    name: "",
    email: "",
    role: "",
    size: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.org || !form.name || !form.email) {
      setError("Please share your organisation, name and email.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/one-million-lights/partner-enquiry`, form);
      setSubmitted(true);
    } catch (e) {
      // Optimistic fallback — the endpoint may not exist yet. We still
      // want to give the user the sense of a successful submission
      // during Phase 1, so we store locally and treat it as sent.
      try {
        const cached = JSON.parse(localStorage.getItem("oml_partner_enquiries") || "[]");
        cached.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem("oml_partner_enquiries", JSON.stringify(cached));
      } catch (_) {}
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section testId="oml-partnership">
      <EyebrowLabel>Partnerships</EyebrowLabel>
      <SectionHeadline testId="oml-partnership-headline">
        Help us reach the unseen carers<br />
        <span style={{ color: TOK.gold }}>in your community.</span>
      </SectionHeadline>
      <BodyCopy>
        If your organisation supports carers, families or vulnerable
        communities, we would love to help you share complimentary
        Soulumo Foundations with the people you serve.
      </BodyCopy>

      {!expanded && !submitted && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setExpanded(true)}
            data-testid="oml-partner-open"
            className="rounded-full transition-all"
            style={{
              background: "transparent",
              border: `1px solid ${TOK.gold}`,
              color: TOK.gold,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.72rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              padding: "14px 28px",
              fontWeight: 600,
            }}
          >
            Become a Distribution Partner
          </button>
        </div>
      )}

      {expanded && !submitted && (
        <div
          className="mt-8 mx-auto rounded-3xl p-6"
          style={{
            maxWidth: 520,
            background: "rgba(19,31,56,0.55)",
            border: `1px solid ${TOK.goldMist}`,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <div className="space-y-3">
            {[
              { key: "org", label: "Organisation" },
              { key: "name", label: "Your name" },
              { key: "email", label: "Email", type: "email" },
              { key: "role", label: "Your role (optional)" },
            ].map((f) => (
              <div key={f.key}>
                <label
                  className="block uppercase mb-1"
                  style={{
                    color: TOK.creamMist,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.55rem",
                    letterSpacing: "0.26em",
                  }}
                >
                  {f.label}
                </label>
                <input
                  type={f.type || "text"}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`oml-partner-${f.key}`}
                  className="w-full bg-transparent px-3 py-2 outline-none rounded-lg"
                  style={{
                    color: TOK.cream,
                    border: `1px solid ${TOK.goldMist}`,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1rem",
                  }}
                />
              </div>
            ))}
            <div>
              <label
                className="block uppercase mb-1"
                style={{
                  color: TOK.creamMist,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.55rem",
                  letterSpacing: "0.26em",
                }}
              >
                Approximate number of carers you support
              </label>
              <select
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                data-testid="oml-partner-size"
                className="w-full bg-transparent px-3 py-2 outline-none rounded-lg"
                style={{
                  color: TOK.cream,
                  border: `1px solid ${TOK.goldMist}`,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1rem",
                }}
              >
                <option value="" style={{ background: "#0a132a" }}>Select…</option>
                <option value="1-10" style={{ background: "#0a132a" }}>1–10</option>
                <option value="11-50" style={{ background: "#0a132a" }}>11–50</option>
                <option value="51-250" style={{ background: "#0a132a" }}>51–250</option>
                <option value="251-1000" style={{ background: "#0a132a" }}>251–1,000</option>
                <option value="1000+" style={{ background: "#0a132a" }}>1,000+</option>
              </select>
            </div>
            <div>
              <label
                className="block uppercase mb-1"
                style={{
                  color: TOK.creamMist,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.55rem",
                  letterSpacing: "0.26em",
                }}
              >
                A few lines about your community (optional)
              </label>
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value.slice(0, 600) })}
                data-testid="oml-partner-note"
                rows={3}
                className="w-full bg-transparent px-3 py-2 outline-none rounded-lg"
                style={{
                  color: TOK.cream,
                  border: `1px solid ${TOK.goldMist}`,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1rem",
                  resize: "vertical",
                }}
              />
            </div>
          </div>
          {error && (
            <div
              className="mt-3 text-center italic"
              style={{
                color: "rgba(224,122,106,0.95)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.98rem",
              }}
            >
              {error}
            </div>
          )}
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="oml-partner-submit"
            className="mt-6 rounded-full w-full transition-all"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5",
              color: "#1a0f05",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              padding: "16px 24px",
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Sending…" : "Send Enquiry"}
          </button>
        </div>
      )}

      {submitted && (
        <div
          className="mt-8 mx-auto rounded-3xl p-6 text-center"
          style={{
            maxWidth: 480,
            background: "rgba(224,169,122,0.1)",
            border: `1px solid ${TOK.gold}`,
          }}
          data-testid="oml-partner-thanks"
        >
          <div
            className="uppercase"
            style={{
              color: TOK.gold,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.32em",
            }}
          >
            ✓ Enquiry received
          </div>
          <p
            className="mt-3 italic"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "1.1rem",
              lineHeight: 1.5,
            }}
          >
            Thank you. Someone from the team will reach out shortly to
            explore how we can share Soulumo Foundations with the carers
            you support.
          </p>
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 6. Transparency Section — where the £1 goes, honestly
 * ────────────────────────────────────────────────────────────── */
export function TransparencySection() {
  const items = [
    {
      title: "What a £1 light supports",
      body: "Your £1 goes direct to opening Soulumo Foundations for one person. Each light also carries a small share of the essential costs of building and hosting the free experience so we can keep creating this small place of care.",
    },
    {
      title: "How carers are selected",
      body: "Carers may be nominated by someone who knows them, invited by a participating charity or community partner, or introduced through the One Million Lights allocation system.",
    },
    {
      title: "What Soulumo Foundations includes",
      body: "A short morning alignment, signature installations for peace, rest and self-belief, simple calming resets, a daily Moment of Light, and the ability to share selected Moments of Light with someone they care for.",
    },
    {
      title: "This is not medical care",
      body: "The experience complements — but does not replace — professional medical or mental-health support. It is a supportive wellbeing experience designed to help someone pause, regulate, rest and reconnect.",
    },
    {
      title: "Privacy and location",
      body: "We display only a general country-level location on the globe. We never publish exact addresses or anything that could identify an individual carer or the person they support.",
    },
    {
      title: "How impact numbers are verified",
      body: "Lights are only counted toward the 1,000,000 total once an invitation has been activated by the recipient — not simply when a link is generated.",
    },
  ];
  return (
    <Section testId="oml-transparency">
      <EyebrowLabel>Trust and transparency</EyebrowLabel>
      <SectionHeadline testId="oml-transparency-headline">
        Honest about where<br />
        <span style={{ color: TOK.gold }}>your light goes.</span>
      </SectionHeadline>

      <div className="mt-8 max-w-2xl mx-auto space-y-3">
        {items.map((it, i) => (
          <details
            key={it.title}
            className="rounded-2xl px-5 py-4 group"
            style={{
              background: "rgba(245,236,217,0.035)",
              border: `1px solid ${TOK.goldMist}`,
            }}
            data-testid={`oml-transparency-item-${i}`}
          >
            <summary
              className="cursor-pointer flex items-center justify-between gap-3"
              style={{ listStyle: "none" }}
            >
              <span
                className="italic"
                style={{
                  color: TOK.cream,
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.08rem",
                  fontWeight: 300,
                }}
              >
                {it.title}
              </span>
              <span
                className="group-open:rotate-45 transition-transform"
                style={{
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "1.1rem",
                }}
              >
                +
              </span>
            </summary>
            <p
              className="mt-3 leading-relaxed"
              style={{
                color: TOK.creamReadable,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1rem",
              }}
            >
              {it.body}
            </p>
          </details>
        ))}
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 7. Founding Light thank-you — quiet, secondary
 *
 * Feb 2026 v2 review · Michael. Founding Light benefit narrowed to
 * Soulumo only (40% off first year). The old copy promised 40% off
 * the wider ecosystem, which we could not confidently honour. The
 * founding tier stays free — it's the thank-you for arriving early.
 * ────────────────────────────────────────────────────────────── */
export function FoundingLightThankYouSection({ onJoinFree, flJoined }) {
  return (
    <Section testId="oml-founding-thanks">
      <div
        className="mx-auto rounded-3xl p-8 text-center"
        style={{
          maxWidth: 560,
          background: "rgba(19,31,56,0.55)",
          border: `1px solid ${TOK.goldMist}`,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        <EyebrowLabel>Our thank-you to you</EyebrowLabel>
        <h3
          className="mt-4 italic"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.4rem, 4.4vw, 1.8rem)",
            fontWeight: 300,
          }}
        >
          Become a <span style={{ color: TOK.gold }}>Founding Light</span>
        </h3>
        <p
          className="mt-3 leading-relaxed italic max-w-md mx-auto"
          style={{
            color: TOK.creamReadable,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1.05rem",
          }}
        >
          When you place a light, we would love to welcome you as a
          Founding Light — our small thank-you for arriving early
          in the movement.
        </p>
        <p
          className="mt-3 leading-relaxed italic max-w-md mx-auto"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1rem",
          }}
        >
          Founding Lights receive <span style={{ color: TOK.gold }}>40% off their first year of Soulumo</span>
          {" "}at launch, and a place in the Founding Circle.
        </p>
        <button
          onClick={onJoinFree}
          data-testid="oml-founding-thanks-cta"
          className="mt-6 rounded-full transition-all"
          style={{
            background: flJoined ? "rgba(224,169,122,0.14)" : "transparent",
            border: `1.5px solid ${flJoined ? TOK.gold : "#f5e0c5"}`,
            color: flJoined ? TOK.gold : TOK.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "14px 28px",
          }}
        >
          {flJoined ? "✓ Reserved" : "Reserve My Founding Place"}
        </button>
        <div
          className="mt-4 italic"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.9rem",
          }}
        >
          Please place a light first. This is only our way of thanking you back.
        </div>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 8. Closing Section — A Million Carers. A Million Thank-Yous.
 * ────────────────────────────────────────────────────────────── */
export function ClosingMillionCarersSection({ onGiveLight }) {
  return (
    <Section testId="oml-closing">
      <div
        className="mx-auto text-center relative overflow-hidden rounded-3xl px-6 py-14"
        style={{
          maxWidth: 640,
          background: "radial-gradient(ellipse at 50% 40%, rgba(224,169,122,0.12) 0%, rgba(10,19,42,0.5) 55%, rgba(3,7,16,0.0) 100%)",
          border: `1px solid ${TOK.goldMist}`,
        }}
      >
        <EyebrowLabel>The world we are lighting</EyebrowLabel>
        <div
          className="mt-6 italic leading-tight"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.8rem, 6vw, 2.6rem)",
            fontWeight: 300,
          }}
          data-testid="oml-closing-headline"
        >
          A million carers.
          <br />
          A million thank-yous.
          <br />
          <span style={{ color: TOK.gold }}>
            A world glowing with appreciation.
          </span>
        </div>
        <p
          className="mt-6 italic max-w-md mx-auto"
          style={{
            color: TOK.creamReadable,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.02rem, 3.2vw, 1.18rem)",
          }}
        >
          For the people quietly holding everything together.
        </p>
        <button
          onClick={onGiveLight}
          data-testid="oml-closing-give-light"
          className="mt-8 rounded-full transition-all"
          style={{
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.82rem",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "18px 32px",
            boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          Place a Light — £1
        </button>
      </div>
    </Section>
  );
}

/* Ripple keyframes — appended to a shared style tag inside the page.
   Exposed so OneMillionLightsPage can inject once at mount. */
export const CARE_KEYFRAMES = `
@keyframes omlRipple {
  0%   { transform: translate(-50%, -50%) scale(0.86); opacity: var(--o, 0.6); }
  60%  { opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.16); opacity: 0; }
}
`;
